import { Injectable } from '@angular/core';

/**
 * Persistenter Debug-Log in localStorage (Ring-Buffer, max 50 Einträge).
 *
 * Zweck: Der User kann in der Browser-Konsole `__hmDebugLog()` aufrufen
 * (oder per `localStorage.getItem('hm:debugLog')` abrufen), um alle kürzlichen
 * HTTP-Fehler / Error-Toasts zu sehen — inkl. Method, URL, Status, Detail.
 *
 * Wird vom `api.interceptor` bei jedem Non-2xx-Response befüllt.
 */
export interface DebugLogEntry {
  ts: string;
  level: 'error' | 'warn' | 'info';
  kind: 'http' | 'toast' | 'custom';
  message: string;
  method?: string;
  url?: string;
  status?: number;
  detail?: string | null;
}

const LS_KEY = 'hm:debugLog';
const MAX_ENTRIES = 50;

@Injectable({ providedIn: 'root' })
export class DebugLogService {
  constructor() {
    try {
      (window as unknown as Record<string, unknown>)['__hmDebugLog'] = () => this.dump();
      (window as unknown as Record<string, unknown>)['__hmDebugLogClear'] = () => this.clear();
    } catch {
      // no-op (SSR, restricted env)
    }
  }

  logHttpError(method: string, url: string, status: number, detail: string | null): void {
    this.append({
      ts: new Date().toISOString(),
      level: 'error',
      kind: 'http',
      message: `HTTP ${status} ${method} ${url}${detail ? ' — ' + detail : ''}`,
      method,
      url,
      status,
      detail,
    });
    try {
      // eslint-disable-next-line no-console
      console.error(`[hm] HTTP ${status} ${method} ${url}`, detail ?? '');
    } catch {
      /* no-op */
    }
  }

  logToast(level: 'error' | 'warn' | 'info', message: string): void {
    this.append({ ts: new Date().toISOString(), level, kind: 'toast', message });
  }

  /**
   * Programm- und Laufzeitfehler ohne HTTP-Ursprung — fehlgeschlagene Berechnungen,
   * unerwartete Zustaende, gefangene Ausnahmen.
   *
   * Warum ueber diesen Dienst und nicht direkt per `console.error`: der Eintrag landet
   * zusaetzlich im Ringpuffer und ist damit spaeter ueber `__hmDebugLog()` abrufbar —
   * eine Konsolen-Zeile ist weg, sobald der Nutzer den Tab neu laedt, und genau dann
   * meldet er den Fehler. Ausgegeben wird trotzdem, aber von der EINEN Stelle im
   * Projekt, an der `console` erlaubt ist.
   */
  logError(quelle: string, nachricht: string, kontext?: unknown): void {
    this.protokolliere('error', quelle, nachricht, kontext);
  }

  /** Wie {@link logError}, aber fuer Auffaelligkeiten, die den Ablauf nicht abbrechen. */
  logWarning(quelle: string, nachricht: string, kontext?: unknown): void {
    this.protokolliere('warn', quelle, nachricht, kontext);
  }

  private protokolliere(level: 'error' | 'warn', quelle: string, nachricht: string, kontext?: unknown): void {
    this.append({
      ts: new Date().toISOString(),
      level,
      kind: 'custom',
      message: `[${quelle}] ${nachricht}`,
      detail: kontext === undefined ? null : this.alsText(kontext),
    });
    try {
      // eslint-disable-next-line no-console
      console[level](`[hm] ${quelle}: ${nachricht}`, kontext ?? '');
    } catch {
      /* no-op */
    }
  }

  /** Kontext fuer den Ringpuffer verflachen; `localStorage` nimmt nur Text. */
  private alsText(wert: unknown): string {
    if (wert instanceof Error) return `${wert.name}: ${wert.message}`;
    if (typeof wert === 'string') return wert;
    try {
      return JSON.stringify(wert) ?? String(wert);
    } catch {
      return String(wert);
    }
  }

  dump(): DebugLogEntry[] {
    return this.read();
  }

  clear(): void {
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* no-op */
    }
  }

  private append(entry: DebugLogEntry): void {
    try {
      const list = this.read();
      list.push(entry);
      while (list.length > MAX_ENTRIES) list.shift();
      localStorage.setItem(LS_KEY, JSON.stringify(list));
    } catch {
      /* no-op */
    }
  }

  private read(): DebugLogEntry[] {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
